/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Injectable} from '@angular/core';
import {forkJoin, map, Observable, of, switchMap} from 'rxjs';
import * as _ from 'lodash';

import {WorkspaceRegistryService} from '../../components/workspace-registry.service';
import {AdnApiClientService} from '../common/controller/adn-api-client.service';
import {
  AdminObjectState,
  SignalAdminResourceApiBase,
  SignalAdminResourceListParams,
  SignalAdminResourceModel,
  SignalAdminResourcePage
} from '../common/controller/signal-admin-resource-api';

export interface WorkspaceModel extends SignalAdminResourceModel {
  [key: string]: any;
}

@Injectable({providedIn: 'root'})
export class WorkspaceApiService extends SignalAdminResourceApiBase<WorkspaceModel> {
  constructor(client: AdnApiClientService, private readonly registry: WorkspaceRegistryService) {
    super(client, 'workspaces');
  }

  override list(params: SignalAdminResourceListParams): Observable<SignalAdminResourcePage<WorkspaceModel>> {
    return super.list(params).pipe(map((page) => ({
      ...page,
      results: (page.results || []).map((model) => this.transformResponse(model))
    })));
  }

  override get(id: string): Observable<WorkspaceModel> {
    return super.get(id).pipe(map((model) => this.transformResponse(model)));
  }

  override save(model: WorkspaceModel): Observable<WorkspaceModel> {
    return super.save(this.transformRequest(model)).pipe(
      map((saved) => this.transformResponse(saved)),
      map((saved) => {
        this.registry.update(saved);
        return saved;
      })
    );
  }

  override setObjectState(id: string, objectState: AdminObjectState): Observable<WorkspaceModel> {
    return super.setObjectState(id, objectState).pipe(
      switchMap(() => this.get(id)),
      map((workspace) => {
      if (objectState === 'ACTIVE') {
        this.registry.update(workspace);
      } else {
        this.registry.remove(workspace);
      }
      return workspace;
      })
    );
  }

  override setObjectStates(items: Array<{id: string; objectState: AdminObjectState}>): Observable<void> {
    return super.setObjectStates(items).pipe(switchMap(() => {
      const activeIds: string[] = [];
      items.forEach((workspace) => {
        if (workspace.objectState === 'ACTIVE') {
          activeIds.push(workspace.id);
        } else {
          this.registry.remove(workspace);
        }
      });
      if (!activeIds.length) {
        return of(undefined);
      }
      return forkJoin(activeIds.map((id) => this.get(id))).pipe(map((workspaces) => {
        workspaces.forEach((workspace) => this.registry.update(workspace));
        return undefined;
      }));
    }));
  }

  private transformRequest(model: WorkspaceModel): WorkspaceModel {
    const transformed = _.cloneDeep(model);
    if (_.has(transformed, 'data') && !_.isString(transformed.data)) {
      transformed.data = JSON.stringify(transformed.data);
    }
    return transformed;
  }

  private transformResponse(model: WorkspaceModel): WorkspaceModel {
    if (_.isString(model?.data)) {
      try {
        return {...model, data: JSON.parse(model.data)};
      } catch (_err) {
        return model;
      }
    }
    return model;
  }
}
